/*
  # Updated Scientific Work page with real publications
  
  1. Changes
    - Replaced fake PDFs with actual research documents from assets folder
    - Changed "View DOI" to "Open PDF" 
    - Updated publication details to match real research papers
    - Added all available publications from the scientific-work folder
    - Organized publications by year and relevance
    - Updated abstracts to reflect actual research content
*/

/*
  # Updated Scientific Work page to use database
  
  1. Changes
    - Updated to fetch publications from database instead of static data
    - Maintained existing UI and functionality
    - Added loading states and error handling
*/

import React, { useEffect } from 'react';
import { Download, Calendar, Users, FileText, ExternalLink } from 'lucide-react';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';
import { ScientificPublication } from '../types/scientific-publications';

const categories = [
  { id: 'all', label: 'All Publications', color: 'bg-[#2A382A]' },
  { id: 'amphibians', label: 'Amphibians', color: 'bg-green-600' },
  { id: 'reptiles', label: 'Reptiles', color: 'bg-orange-600' },
  { id: 'ecology', label: 'Ecology', color: 'bg-blue-600' },
  { id: 'behavior', label: 'Behavior', color: 'bg-purple-600' },
  { id: 'conservation', label: 'Conservation', color: 'bg-red-600' }
];

const ScientificWork = () => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  const [publications, setPublications] = React.useState<ScientificPublication[]>([]);
  const [filteredPublications, setFilteredPublications] = React.useState<ScientificPublication[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const { data, error } = await supabase
        .from('scientific_publications')
        .select('*')
        .order('display_order', { ascending: true })
        .order('year', { ascending: false });

      if (error) throw error;
      setPublications(data || []);
      setFilteredPublications(data || []);
    } catch (err) {
      console.error('Error fetching publications:', err);
      setError('Failed to load publications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPublications(publications);
    } else {
      setFilteredPublications(publications.filter(pub => pub.category === selectedCategory));
    }
  }, [selectedCategory, publications]);

  const getCategoryColor = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat?.color || 'bg-gray-600';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#2A382A] via-[#2A382A] to-[#1F2B1F] text-white">
        {/* Hero Section */}
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-24 md:py-32">
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <h1 className="font-barrio text-4xl md:text-5xl lg:text-6xl mb-8 text-white">
              Scientific Work
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-4xl mx-auto">
              Our research contributions to herpetology and conservation science in Costa Rica.
              <br className="hidden md:block" />
              Documenting biodiversity and advancing our understanding of tropical ecosystems.
            </p>
          </div>

          {/* Hero Image */}
          <div className="mt-8 md:mt-8 w-full max-w-4xl mx-auto relative z-10 mb-[-12rem]">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src="/assets/scientific-work/scientific-work.webp"
                alt="Young frog representing our growing scientific work"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container max-w-[95%] xl:max-w-[80vw] mx-auto px-4 pt-32 pb-16 bg-white rounded-t-[2rem] text-[#2A382A]">
          {/* Introduction */}
          <section className="mb-12">
            <div className="prose prose-lg prose-headings:text-[#2A382A] prose-p:text-[#2A382A]/80 max-w-none">
              <h2 className="font-barrio text-3xl mb-6">Research & Publications</h2>
              <p className="text-lg mb-8">
                Just like the baby frog in the picture, we are steadily growing and working towards a deeper understanding of our herpetofauna. Our team is dedicated to documenting and exploring new areas in the Osa Peninsula and other key regions of the country to uncover new populations, gather information, and enhance our knowledge of natural history.
              </p>
              <p className="text-lg">
                All the valuable insights we have collected are the result of our team's hard work and passion. By choosing to take a tour with us, you contribute to the resources that allow us to continue exploring and publishing crucial information. Thank you for supporting our mission and being a part of our journey.
              </p>
            </div>
          </section>

          {/* Category Filter */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Filter by Category</h3>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-white font-medium transition-all hover:scale-105 ${
                    selectedCategory === category.id 
                      ? category.color 
                      : 'bg-gray-400 hover:bg-gray-500'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </section>

          {/* Publications Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A382A]"></div>
              <p className="mt-4 text-[#2A382A]/70">Loading publications...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : filteredPublications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#2A382A]/70">No publications found.</p>
            </div>
          ) : (
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {filteredPublications.map((publication) => (
                <article
                  key={publication.id}
                  className="bg-[#F2F3F4] rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                >
                  {/* Image */}
                  <div className="aspect-[16/9] relative overflow-hidden">
                    <iframe
                      src={`${publication.pdf_url}#toolbar=0&navpanes=0&scrollbar=0`}
                      title={`Preview of ${publication.title}`}
                      className="w-full h-full border-0 pointer-events-none"
                      style={{ 
                        transform: 'scale(1.2)',
                        transformOrigin: 'top left',
                        width: '83.33%',
                        height: '83.33%'
                      }}
                    />
                    <div className="absolute inset-0 bg-transparent hover:bg-black/10 transition-colors cursor-pointer" />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${getCategoryColor(publication.category)}`}>
                        {categories.find(c => c.id === publication.category)?.label}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#2A382A] mb-3 leading-tight">
                      {publication.title}
                    </h3>

                    {/* Authors and Journal */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-sm text-[#2A382A]/70 mb-1">
                        <Users className="w-4 h-4" />
                        <span>{publication.authors.join(', ')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#2A382A]/70 mb-1">
                        <FileText className="w-4 h-4" />
                        <span>{publication.journal}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#2A382A]/70">
                        <Calendar className="w-4 h-4" />
                        <span>{publication.year}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <a
                        href={publication.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors flex-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open PDF
                      </a>
                      <a
                        href={publication.pdf_url}
                        download
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-[#2A382A]/20 text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors flex-1"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          )}

          {/* Research Collaboration */}
          <section className="mt-16 bg-[#2A382A] rounded-xl p-8 text-white hidden">
            <h2 className="font-barrio text-3xl mb-6">Research Collaborations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">University Partners</h3>
                <ul className="space-y-2 text-white/80">
                  <li>• University of Costa Rica (UCR)</li>
                  <li>• University of Bern, Switzerland</li>
                  <li>• Smithsonian Tropical Research Institute</li>
                  <li>• Organization for Tropical Studies (OTS)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Research Focus Areas</h3>
                <ul className="space-y-2 text-white/80">
                  <li>• Amphibian and reptile biodiversity</li>
                  <li>• Predator-prey relationships</li>
                  <li>• Color pattern variations</li>
                  <li>• Behavioral ecology</li>
                  <li>• Species distribution records</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact for Research */}
          <section className="mt-12 text-center hidden">
            <h2 className="font-barrio text-3xl mb-4">Interested in Collaboration?</h2>
            <p className="text-lg text-[#2A382A]/80 mb-6 max-w-2xl mx-auto">
              We welcome collaborations with researchers, students, and institutions interested in 
              Costa Rican herpetofauna and tropical ecology.
            </p>
            <a
              href="mailto:research@sierpefrogs.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
            >
              <FileText className="w-5 h-5" />
              Contact for Research Opportunities
            </a>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ScientificWork;